//==========================================================================
//   Component: Active Table Row
//==========================================================================

//   Purpose: Allows user to expand/collapse row in the active subscriptions table

//   Properties: 
//   - rowData = {data used to populate the rows in the table}

//   Example: 
//   <ActiveTableRow
//      rowData={row}
//   ></ActiveTableRow>

//------------------------------------------------------


//Libraries
import React, {useReducer, useContext, useState, useEffect} from 'react'
import { useNavigate } from 'react-router-dom';
import { Form, Modal } from 'react-bootstrap'

//Contexts
import {SetError} from '../../../Library/GlobalContexts'

//Components
import GetImage from '../../../Components/GetImage/GetImage';
import RatingsSimple from '../../../Components/RatingsCard/RatingsSimple';
import ErrorHandler from '../../../Components/ErrorHandler/ErrorHandler';
import NotificationLabel from '../../../Components/NotificationLabel/NotificationLabel';
import PricingTotal from '../../../Components/PricingTotal/PricingTotal';

//Functions
import GetDocument from '../../../Library/GetDocument';
import WriteDocument from '../../../Library/WriteDocument';
import { reStringUnsignedInteger, reString } from '../../../Library/ValidateUserInput';

//Images
import ExpandIcon from '../../../Components/Images/Icon_Expand_Black.svg'

//CSS
import '../MySubscriptions.css'


export default function ActiveTableRow(props) {

    //------------------------------------------------------
    //  Extract props
    //------------------------------------------------------

        var rowData = props.rowData
        const projectCodeList = props.projectCodeList

    //------------------------------------------------------
    //  useContexts & Functions
    //------------------------------------------------------
        
        const setError = useContext(SetError);
        const navigate = useNavigate();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

        //Handle the status of the component > 'onload', 'edit', 'pending', 'error-fatal'
        const [requestType, setRequestType] = useState('onload');

        //Used to determine if the row is expanded/collapsed
        const [rowExpanded, setRowExpanded] = useState(false);

        //Used to determine if the invalid PTE/currency confirmation modal is visible
        const [pteModalVisible, setPteModalVisible] = useState(false);

        //Hold list of all valid task numbers
        const [taskNumberList, setTaskNumberList] = useState([]);

        //Holds list of matching PTEs user can select from
        const [projectCodeOptions, setProjectCodeOptions] = useState([]);
        const [taskNumberOptions, setTaskNumberOptions] = useState([]);

        //Holds Oracle project name valid project code
        const [oracleProjectName, setOracleProjectName] = useState('');

        //Warning for invalid PTE
        const [pteWarning, setPteWarning] = useState('');

        //Document to submit to Firestore
        const [submitDoc, setSubmitDoc] = useState();

        //Do an initial check to start off with
        const [initialLoad, setInitialLoad] = useState(true);

        //If user cancels PTE edit, triggers a reload when PTE has been reset
        const [reload, setReload] = useState(false);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

        //Holds the current PTE values while editing
        const [pteValues, setPteValues] = useReducer(
            (state, newState) => ({ ...state, ...newState }),
            {
                projectCode: rowData?.subscriberprojectcode === undefined ? '' : (rowData.subscriberprojectcode),
                taskNumber: rowData?.subscribertasknumber === undefined ? '' : (rowData.subscribertasknumber),
                expenditureType: rowData?.subscriberexpendituretype === undefined ? '' : (rowData.subscriberexpendituretype)
            }
        );

        //Indicates whether PTE values were auto-generated
        const [pteAutoSet, setPteAutoSet] = useReducer(
            (state, newState) => ({ ...state, ...newState }),
            {
                taskNumber: false,
                expenditureType: false
            }
        );

        //Indicates whether PTE values are valid or not
        const [validPteValues, setValidPteValues] = useReducer(
            (state, newState) => ({ ...state, ...newState }),
            {
                projectCode: null, // Set to null until initial validation is complete
                taskNumber: null, // Set to null until initial validation is complete
                expenditureType: true // Always true until expenditure type validation is possible
            }
        );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

        // No valid task number yet, so clear everything related to a task number
        function clearTaskNumberInfo() {
            setValidPteValues({ taskNumber: false });
            setTaskNumberOptions([]);
        }

        //------------------------------------------------------

        // No valid project code yet, so clear everything related to a project code
        function clearProjectCodeInfo() {
            setValidPteValues({ projectCode: false });
            setTaskNumberList([]);
            setProjectCodeOptions([]);
            setOracleProjectName('');
            setTaskNumberOptions([]);

            // On initial load, need to leave taskNumber as undefined
            // As we want to delay PTE validation warnings until all checks are complete
            if (!initialLoad) {
                setValidPteValues({ taskNumber: false });
            }
        }
        
        //------------------------------------------------------

        // Function to validate project code
        // If exact match, get info and task numbers
        function processProjectCode(value) {

            setPteValues({ projectCode: value })

            //New data, so clear PTE and currency warning
            setPteWarning('');

            // Any change to project code will clear the auto set task number, expenditure type and currency
            if (pteAutoSet.taskNumber) {
                setPteAutoSet({ taskNumber: false })
                setPteValues({ taskNumber: '' })
            }

            if (pteAutoSet.expenditureType) {
                setPteAutoSet({ expenditureType: false })
                setPteValues({ expenditureType : '' })
            }

            // Field is empty > clear info derived from the project code
            if (value.length <= 0) {
                clearProjectCodeInfo()
                return;
            }
            
            // Only bother processing if there's at least three characters
            if (value.length < 3) {
                clearProjectCodeInfo()
                return;
            }

            // Get list of matching project codes and show in project code datalist dropdown
            const searchResult = projectCodeList.filter(x => x.startsWith(value))
            setProjectCodeOptions(searchResult)
    
            // Exact match
            if(searchResult.length === 1 && searchResult[0] === value) {        
                // Get document for project code from oracleProjects collection
                GetDocument('oracleProjects', value)
                .then((results) => {
                    // Project code has document, so accept as valid project code
                    setValidPteValues({ projectCode: true })

                    // Missing/invalid task number list, so cannot validate
                    if(results?.taskNumbers === undefined || results.taskNumbers.constructor.name !== 'Object') {
                        setTaskNumberList([]);
                        clearTaskNumberInfo();
                        return;
                    }
                    
                    // Show the complete list of task numbers in the task number datalist dropdown
                    setTaskNumberList(results.taskNumbers)
        
                    // Set the project name if we have it
                    if(results.projectName !== undefined) setOracleProjectName(results.projectName)
                    
                // Something went wrong with this project code info, so pretend it's not valid
                }).catch((error) => {
                    clearProjectCodeInfo()
                    return;
                })
        
                // Haven't pinpointed on exact project code yet
                } else {
                    setValidPteValues({ projectCode: false })

                    // On initial load, need to leave taskNumber as undefined
                    // As we want to delay PTE validation warnings until all checks are complete
                    if (!initialLoad) {
                        setValidPteValues({ taskNumber: false });
                    }
    
                    setTaskNumberList([])
                    setTaskNumberOptions([])
                    setOracleProjectName('')
                }

            }
    
        //------------------------------------------------------
        
        // Function to validate task number
        function processTaskNumber(value, autoSet) {

            setPteValues({ taskNumber: value })

            //New data, so clear PTE warning
            setPteWarning('');

            // Any change to task number will clear the auto set expenditure type
            if (pteAutoSet.expenditureType) {
                setPteAutoSet({ expenditureType: false })
                setPteValues({ expenditureType: '' })
            }

            // Indicates whether the field was changed automatically or by the user
            setPteAutoSet({ taskNumber: autoSet })

            // We don't have a task number list, so we cannot search for it
            if(taskNumberList.length <= 0) {
                clearTaskNumberInfo();
                return;
            }
    
            // No value entered for task number, so show all valid task numbers
            if(value.length <= 0) {
                setTaskNumberOptions(Object.keys(taskNumberList))
                setValidPteValues({ taskNumber: false })
                return;
            }
    
            // Search for task number
            const searchResult = Object.keys(taskNumberList).filter(x => x.startsWith(value))
            setTaskNumberOptions(searchResult)
    
            // Exact match
            if(searchResult.length === 1 && searchResult[0] === value) {
    
                setValidPteValues({ taskNumber: true })
        
                // If no expenditure type provided by user and task number is 11050
                // set expenditure type to 'ICT Costs' automatically
                if((pteValues.expenditureType.length <= 0) && value === '11050') {
                    processExpenditureType('ICT Costs', true) // autoSet = true
                }
    
            // Multiple matches
            } else {
                setValidPteValues({ taskNumber: false })
            }

        }
            
        //------------------------------------------------------
        
        // Function to process expenditure type changes
        function processExpenditureType(value, autoSet) {

            setPteValues({ expenditureType: value })

            //New data, so clear PTE warning
            setPteWarning('');

            // Indicates whether the field was changed automatically or by the user
            setPteAutoSet({ expenditureType: autoSet })

        }

        //------------------------------------------------------
        
        // Function to actually submit request to edit subscription
        function submitRequest() {

            //Show pending screen to user
            setRequestType('pending');
            setPteModalVisible(false);

            // Check for missing request
            if (submitDoc === undefined || submitDoc.length <= 0) {
                setError("Could not submit request. Request document is empty")
                setRequestType('error-fatal')
                return;
            }

            WriteDocument('subscriptions', rowData.subscriptionid, submitDoc, true).then(() =>{

                //Update existing rowData/subscription > Saves a DB call
                rowData.ptechanged = submitDoc.ptechanged;
                rowData.subscriberprojectcode = submitDoc.subscriberprojectcode;
                rowData.subscribertasknumber = submitDoc.subscribertasknumber;
                rowData.subscriberexpendituretype = submitDoc.subscriberexpendituretype;
    
                setRequestType('onload');
    
            }).catch((error) =>{

                setRequestType('error-fatal');
    
                //Submit error to global store > This will be captured by ErrorHandler
                setError(`Publishing product has failed, error: ${error}`);

            });
        }

        //------------------------------------------------------

        //Handle request type
        function handleRequestType(requestType){

            //If requestType is 'onload'
            if(requestType === 'onload' || requestType === 'pending'){
                return (
                    <div className='ActiveTableRow-Expanded'>

                        {
                            requestType === 'pending' ? (
                                <div>
                                    Saving...
                                </div>
                            ) : (
                                /* Project Code, Task Number & Expenditure Type */
                                <div className='ActiveTableRow-PTE'>
                                    <p className='ActiveTableRow-FieldLabel'>Project Code</p>
                                    {rowData?.subscriberprojectcode}

                                    <p className='ActiveTableRow-FieldLabel'>Task Number</p>
                                    {rowData?.subscribertasknumber}

                                    <p className='ActiveTableRow-FieldLabel'>Expenditure Type</p>
                                    {rowData?.subscriberexpendituretype}
                                </div>
                            )
                        }

                        {/* Cost & Support */}
                        <div className='ActiveTableRow-Support'>

                            <p className='ActiveTableRow-FieldLabel'>Cost</p>
                            <PricingTotal
                                pricingdiscount={rowData.productobject?.pricingdiscount}
                                pricingamount={rowData.productobject?.pricingamount}
                                pricingfrequency={rowData.productobject?.pricingfrequency}
                                pricingchanged={rowData?.productobject?.pricingchanged}
                            ></PricingTotal>
                        
                            <p className='ActiveTableRow-FieldLabel'>Support</p>
                            <ul>
                                <li>{rowData?.productobject?.supportemail}</li>
                                <li><a target='_blank' rel='noreferrer' href={rowData.productobject?.supportlink}>{rowData.productobject?.supportlink}</a></li>
                            </ul>

                        </div>

                        {/* Buttons */}
                        <div className='ActiveTableRow-Buttons'>
                            <button
                                className='Primary-Button'
                                style={{fontWeight: "500", fontSize: "0.95rem"}}
                                onClick={() => setRequestType('edit')}
                                hidden={rowData.productobject?.pricingamount === "0" || requestType === 'pending'}
                            >
                            Edit Details
                            </button>
                            <button
                                className='Secondary-Button'
                                style={{fontWeight: "500", fontSize: "0.95rem"}}
                                onClick={() => navigate(`/unsubscribe?productid=${rowData?.productid}&subscriptionid=${rowData?.subscriptionid}`)}
                                hidden={requestType === 'pending'}
                            >
                            Unsubscribe
                            </button>
                        </div>

                        {/* Product Ratings */}
                        <div className='ActiveTableRow-Ratings'>
                            Click to rate product
                            <RatingsSimple
                                productid={rowData?.productobject?.productid} 
                            ></RatingsSimple>  
                        </div>
                    </div>
                )
            }

            //If requestType is 'edit'
            if(requestType === 'edit'){
                return (
                    <>
                        <Form className='ActiveTableRow-Expanded' onSubmit={(e) => handleFormSubmit(e)}>

                            {/* Project Code, Task Number & Expenditure Type */}
                            <div className='ActiveTableRow-PTE'>

                                {/* Subscriber Project Code */}
                                <p className='ActiveTableRow-FieldLabel'>Project Code</p>
                                <div style={{width: "50%"}}>
                                    <Form.Group controlId='subscriberprojectcode'>
                                        {}
                                        <Form.Control
                                            name='projectCode'
                                            required
                                            size="sm"
                                            type='text'
                                            pattern={reStringUnsignedInteger}
                                            value={pteValues.projectCode}
                                            onChange={handleChange}
                                            onBlur={handleChangeBlurPte}
                                            list={`activeTableProjectCodeList-${rowData.subscriptionid}`}
                                        ></Form.Control>
                                        <datalist id={`activeTableProjectCodeList-${rowData.subscriptionid}`}>
                                            {projectCodeOptions.map((row, index) => (
                                                <option id={index}>{row}</option>
                                            ))}
                                        </datalist>
                                        { /* Show Oracle project name if we have a valid project code */
                                            oracleProjectName.length > 0 ? (
                                                <div className='ActiveTableRow-Oracle-Project-label'>{oracleProjectName}</div>
                                            ) : (null)
                                        }
                                    </Form.Group>
                                </div>

                                {/* Subscriber Task Number */}
                                <p className='ActiveTableRow-FieldLabel'>Task Number</p>
                                <div style={{width: "50%"}}>
                                    <Form.Group controlId='subscribertasknumber'>
                                        <Form.Control
                                            name='taskNumber'
                                            required
                                            size="sm"
                                            type='text'
                                            pattern={reString}
                                            value={pteValues.taskNumber}
                                            onChange={handleChange}
                                            onBlur={handleChangeBlurPte}
                                            list={`activeTableTaskNumberList-${rowData.subscriptionid}`}
                                            ></Form.Control>
                                        <datalist id={`activeTableTaskNumberList-${rowData.subscriptionid}`}>
                                            {taskNumberOptions.map((row, index) => (
                                                <option id={index}>{row}</option>
                                            ))}
                                        </datalist>
                                    </Form.Group>
                                </div>

                                {/* Subscriber Expenditure Type */}
                                <p className='ActiveTableRow-FieldLabel'>Expenditure Type</p>
                                <div style={{width: "50%"}}>
                                    <Form.Group controlId='subscriberexpendituretype'>
                                        <Form.Control
                                            name='expenditureType'
                                            required
                                            size="sm"
                                            type='text'
                                            pattern={reString}
                                            value={pteValues.expenditureType}
                                            onChange={handleChange}
                                            onBlur={handleChangeBlurPte}
                                            list={`activeTableExpenditureTypeList-${rowData.subscriptionid}`}
                                        ></Form.Control>
                                        <datalist id={`activeTableExpenditureTypeList-${rowData.subscriptionid}`}>
                                        </datalist>
                                    </Form.Group>
                                </div>

                                {/* Warning Label > Invalid PTE */}
                                {
                                // Show PTE invalid warning if applicable
                                pteWarning.length > 0 ? (
                                    <NotificationLabel
                                    type={"warning"}
                                    message={pteWarning}
                                    ></NotificationLabel>
                                ) : (null)
                                }

                            </div>

                            {/* Cost & Support */}
                            <div className='ActiveTableRow-Support'>

                                <p className='ActiveTableRow-FieldLabel'>Cost</p>
                                <PricingTotal
                                    pricingdiscount={rowData.productobject?.pricingdiscount}
                                    pricingamount={rowData.productobject?.pricingamount}
                                    pricingfrequency={rowData.productobject?.pricingfrequency}
                                    pricingchanged={rowData?.productobject?.pricingchanged}
                                ></PricingTotal>
                            
                                <p className='ActiveTableRow-FieldLabel'>Support</p>
                                <ul>
                                    <li>{rowData?.productobject?.supportemail}</li>
                                    <li><a target='_blank' rel='noreferrer' href={rowData.productobject?.supportlink}>{rowData.productobject?.supportlink}</a></li>
                                </ul>

                            </div>

                            {/* Buttons */}
                            <div className='ActiveTableRow-Buttons'>
                                <button className='Primary-Button' type='submit'> Save </button>
                                <button onClick={() => setRequestType('onload')} className='Secondary-Button'> Cancel </button>
                            </div>

                            {/* Product Ratings */}
                            <div className='ActiveTableRow-Ratings'> 
                                Click to rate product         
                                <RatingsSimple
                                    productid={rowData?.productobject?.productid} 
                                ></RatingsSimple>  
                            </div>

                        </Form>

                        {/* Invalid PTE modal */}
                        <Modal show={pteModalVisible} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
                            <Modal.Header>
                            <Modal.Title id="contained-modal-title-vcenter">
                                Warning
                            </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {
                                    // Show PTE invalid warning if applicable
                                    pteWarning.length > 0 ? (
                                        <p>{pteWarning}</p>
                                    ) : (null)
                                }
                                <p>If you wish to continue regardless, click <b>Continue</b>.</p>
                                <p>If you would like to go back without saving your changes, click <b>Cancel</b>.</p>
                            </Modal.Body>
                            <Modal.Footer>
                                <button className='Primary-Button' variant="primary" onClick={submitRequest}>
                                    Continue
                                </button>
                                <button className='Secondary-Button' variant="secondary" onClick={(e) => setPteModalVisible(false)}>
                                    Cancel
                                </button>
                            </Modal.Footer>
                        </Modal>
                    </>
                )
            }

            //If requestType is 'error-fatal'
            if(requestType === 'error-fatal'){
                return (
                    <ErrorHandler></ErrorHandler>
                )
            }

        }

        //------------------------------------------------------

        // Checks for PTE/currency errors and show warnings
        function pteValidation() {

            // Don't bother showing PTE warning if the fields haven't been filled out yet
            if (pteValues.projectCode.length <= 0 || pteValues.taskNumber.length <= 0 || pteValues.expenditureType.length <= 0) return

            // Invalid project code
            if (!(validPteValues.projectCode)) {

                setPteWarning(`Unknown Oracle Project Code '${pteValues.projectCode}' provided. You may proceed to submit however we recommended checking that your Oracle Project Code is correct.`)

            // Invalid task number
            } else if (!(validPteValues.taskNumber)) {

                setPteWarning(`Unknown Task Number '${pteValues.taskNumber}' provided. You may proceed to submit however we recommended checking that your Oracle Task Number is correct.`)

            // Invalid expenditure type
            } else if (!(validPteValues.expenditureType)) {

                setPteWarning(`Unknown Expenditure Type '${pteValues.expenditureType}' provided. You may proceed to submit however we recommended checking that your Oracle Expenditure Type is correct.`)

            }

        }


    //------------------------------------------------------
    //  Event Handler > On Exit PTE Component (onBlur)
    //------------------------------------------------------
  
        //Event Handler for field changes 
        function handleChangeBlurPte(event) {

            const name = event.target.name;
        
            // Process if PTE details have changed
            if (name === 'projectCode' || name === 'taskNumber' || name === 'expenditureType') {

                pteValidation()

            }
        }

    //------------------------------------------------------
    //  Event Handler (Validate form values)
    //------------------------------------------------------

        function handleChange(e) {
            const name = e.target.name
            const value = e.target.value

            if (name === 'projectCode') processProjectCode(value)
            else if (name === 'taskNumber') processTaskNumber(value, false) // autoSet = false
            else if (name === 'expenditureType') processExpenditureType(value, false) // autoSet = false
        }

    //------------------------------------------------------
    //  Event Handler (Update the users task, project code and type)
    //------------------------------------------------------

        function handleFormSubmit(e){
        
            //Prevent reload & Enter
            e.preventDefault();
            if (e.keyCode === 13) {
                return;
            }

            setSubmitDoc({
            'subscriberprojectcode': e.target.subscriberprojectcode.value,
            'subscribertasknumber': e.target.subscribertasknumber.value,
            'subscriberexpendituretype': e.target.subscriberexpendituretype.value,
            'ptechanged':
                rowData?.ptechanged ||
                rowData.subscriberprojectcode !== e.target.subscriberprojectcode.value ||
                rowData.subscribertasknumber !== e.target.subscribertasknumber.value ||
                rowData.subscriberexpendituretype !== e.target.subscriberexpendituretype.value,
            })

        }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

        // Perform first time validations
        useEffect(() => {

            // Haven't initialised yet, so no point validating project code
            if (pteValues === undefined) return;
            if (pteValues.projectCode === undefined) return;
            if (projectCodeList === undefined) return;

            // Only run on initial load of PTE data
            if (initialLoad) {
        
                // Haven't kicked off any checks yet, so let's do that
                if (validPteValues.projectCode === null) processProjectCode(pteValues.projectCode)
                
                // Initial PTE checks are done, so let's show warnings if there is a problem
                if (validPteValues.projectCode !== null && validPteValues.taskNumber !== null) {
                    pteValidation();
                    setInitialLoad(false)
                }
            }
        
            // Task number will be validated automatically if project code is valid
        
        // eslint-disable-next-line
        }, [validPteValues, projectCodeList]);
        
        //------------------------------------------------------

        // Actions when cancelling a PTE edit
        useEffect(() => {

            // Don't run this on initial load
            if (pteValues === undefined) return;
            if (initialLoad) return;

            // Reset PTE data if user cancels changes
            if (requestType === 'onload') {

                setPteValues({
                    projectCode: rowData.subscriberprojectcode,
                    taskNumber: rowData.subscribertasknumber,
                    expenditureType: rowData.subscriberexpendituretype
                })

                setPteAutoSet({
                    taskNumber: false,
                    expenditureType: false
                })

                // Trigger a future re-validation
                setInitialLoad(true)
                setReload(true)

            }
        
        // eslint-disable-next-line
        }, [requestType]);

        //------------------------------------------------------

        // Force a re-check of PTE validity of user cancelled PTE edit
        useEffect(() => {

            if (reload) {

                setReload(false)

                // By setting this, will kick off "initial" validation
                setValidPteValues({
                    projectCode: null, // Set to null until initial validation is complete
                    taskNumber: null, // Set to null until initial validation is complete
                    expenditureType: true // Always true until expenditure type validation is possible    
                })

            }


        // eslint-disable-next-line
        }, [reload]);

        //------------------------------------------------------
            

        // When the task numbers for a project code are made available
        useEffect(() => {

            // Haven't initialised yet, so no point validating task number
            if (pteValues === undefined) return;
            if (pteValues.taskNumber === undefined) return;
            if (validPteValues.projectCode === null) return;

            // If no task number provided by user and 11050 is a valid task number for this project
            // set it automatically
            if(pteValues.taskNumber.length <= 0 && Object.keys(taskNumberList).includes('11050')) {
                processTaskNumber('11050', true) // autoSet = true
            
            // Re-check validity of whatever task is there
            // Cannot do full validation in case it's a new form load
            } else {
                processTaskNumber(pteValues.taskNumber, false) //autoSet = false
            }

        // eslint-disable-next-line
        }, [taskNumberList]);

        //------------------------------------------------------

        // When submission document is created, perform warning checks then submit request
        useEffect(() => {

            // Haven't initialised yet, so nothing to submit
            if (submitDoc === undefined || submitDoc.length <= 0) return;

            // Need to show warning modal if there's a currency mismatch or invalid PTE
            if (!pteModalVisible && pteWarning.length > 0) {
                setPteModalVisible(true)
                return;
            }

            // No warnings, so send request to Firestore
            submitRequest()

        // eslint-disable-next-line
        }, [submitDoc]);
    

    //------------------------------------------------------
    //  Return HTML
    //------------------------------------------------------

        //------------------------------------------------------
        //  Row Expanded
        //------------------------------------------------------
        
        if(rowExpanded) {
            return (
                <>
                    <tr className='ExpandableTable-Container-Row' onClick={() => setRowExpanded(false)}>
                        <td style={{textAlign: "center"}}>
                            <div className='MySubscriptions-Pending-Logo'>
                                <GetImage iconurl={rowData.productobject?.iconurl}></GetImage>
                            </div>
                        </td>
                        <td>{rowData.productobject?.name[0].toUpperCase() + rowData.productobject?.name.slice(1)}</td>
                        <td>
                            {rowData.subscriptionid}
                        </td>
                        <td>
                            {rowData.startdate}
                        </td>
                        <td style={{textAlign: "center"}}>
                            <img className="Expand-Icon" src={ExpandIcon} style={{transform: "rotate(90deg)"}} alt="Expand_Icon"></img>
                        </td>
                    </tr> 
                    
                    {/* Row Expanded > Subscription Details */}
                    <tr className='ExpandableTable-Container-Row-Expanded'>
                        <td colSpan="5">
                            {handleRequestType(requestType)}
                        </td>
                    </tr>
                </>
            )
        }

        //------------------------------------------------------
        //  Row Collapsed
        //------------------------------------------------------

        else {
            return (
                <tr className='ExpandableTable-Container-Row' onClick={() => setRowExpanded(true)}>
                    <td style={{textAlign: "center"}}>
                        <div className='MySubscriptions-Pending-Logo'>
                            <GetImage iconurl={rowData.productobject?.iconurl}></GetImage>
                        </div>
                    </td>
                    <td>{rowData.productobject?.name[0].toUpperCase() + rowData.productobject?.name.slice(1)}</td>
                    <td>
                        {rowData.subscriptionid}
                    </td>
                    <td>
                        {rowData.startdate}
                    </td>
                    <td style={{textAlign: "center"}}>
                        <img className="Expand-Icon" src={ExpandIcon} alt="Expand_Icon"></img>
                    </td>
                </tr>
            )
        }
        
}
